'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import SeoHead from '@/components/seo/SeoHead';
import { SEO_PAGES } from '@/lib/seo';
import { DEFAULT_DESCRIPTION } from '@/lib/site';
import { Moon, Sun } from 'lucide-react';
import BrandLogo from '@/components/shared/BrandLogo';
import { motion } from 'framer-motion';

export default function UserAuthPage({ title, subtitle, children, secondaryText, secondaryHref, secondaryLabel }) {
  const { t } = useTranslation('panel');
  const router = useRouter();
  const [theme, setTheme] = useState('dark');
  const publicSeo = SEO_PAGES[router.pathname];

  useEffect(() => {
    const t = localStorage.getItem('panel-theme') === 'light' ? 'light' : 'dark';
    setTheme(t);
    document.documentElement.classList.toggle('light-theme', t === 'light');
  }, []);

  function toggleTheme() {
    setTheme((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      document.documentElement.classList.toggle('light-theme', next === 'light');
      localStorage.setItem('panel-theme', next);
      return next;
    });
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-bg-primary">
      {publicSeo ? null : (
        <SeoHead
          title={title}
          description={DEFAULT_DESCRIPTION}
          path={router.pathname}
          noindex
        />
      )}
      <div className="flex justify-end px-4 pt-4">
        <button
          type="button"
          onClick={toggleTheme}
          className="flex items-center gap-2 rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-text-primary transition hover:border-accent-amber/40"
          aria-label={t('auth.page.ariaTheme')}
        >
          {theme === 'dark' ? <Sun size={16} className="text-accent-amber" /> : <Moon size={16} className="text-text-muted" />}
          <span className="text-text-muted">
            {theme === 'dark' ? t('auth.page.themeLight') : t('auth.page.themeDark')}
          </span>
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-[440px] rounded-2xl border border-border bg-bg-card p-6 sm:p-8"
        >
          <div className="mb-6 flex justify-center">
            <BrandLogo href="/" size="auth" variant="auto" />
          </div>
          <h1 className="font-display text-2xl text-text-primary">{title}</h1>
          <p className="mt-1 text-sm text-text-muted">{subtitle}</p>
          <div className="mt-6">{children}</div>
          <p className="mt-4 text-center text-sm text-text-muted">
            {secondaryText}{' '}
            <Link href={secondaryHref} className="text-accent-amber">
              {secondaryLabel}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
